/**
 * useCommentsIndex — every project's comments in ONE fetch, indexed by row id.
 *
 * The Comments column in the Opportunities and Projects tables reads this
 * rather than fetching per row, which on a full table would be hundreds of
 * requests. Same shape and same reasoning as useProjectTasksIndex.
 *
 * Adding or deleting a comment anywhere calls notifyCommentsChanged(), so the
 * column updates without a page refresh — including a right-click quick-add,
 * which never opens the record at all.
 *
 * Someone else's note re-pulls it too (src/live), so its reads can overlap:
 * they go through latestReads - the newest answer GOT is shown, an older one
 * landing late never is - and a refused or failed read changes nothing. A
 * 429 or 502 while every open tab re-reads must not empty the column.
 */
import { useCallback, useEffect, useState } from 'react';
import { useApiBase } from '../context/DataScope';
import { latestReads } from '../live/useLive';

const listeners = new Set();

/** Tell every open table to re-pull comments. */
export const notifyCommentsChanged = () => {
  listeners.forEach((fn) => {
    try {
      fn();
    } catch {
      /* a dead listener must not stop the rest */
    }
  });
};

export function useCommentsIndex() {
  const base = useApiBase(); // the KSA tab swaps in its own prefix (context/DataScope.js)
  const [index, setIndex] = useState(() => new Map());
  const [tick, setTick] = useState(0);
  const [reads] = useState(latestReads);

  const reload = useCallback(() => setTick((t) => t + 1), []);
  useEffect(() => {
    listeners.add(reload);
    return () => {
      listeners.delete(reload);
    };
  }, [reload]);

  useEffect(() => {
    const ticket = reads.start();
    fetch(`${base}/projects/comments-index`)
      .then((r) => (r.ok ? r.json() : null))
      .then((rows) => {
        if (!Array.isArray(rows) || !reads.show(ticket)) return; // could not read: the column stays
        const m = new Map();
        for (const c of rows) {
          if (!m.has(c.project_id)) m.set(c.project_id, []);
          m.get(c.project_id).push(c);
        }
        setIndex(m);
      })
      .catch(() => {
        /* offline or refused: the column stays as it is */
      });
  }, [tick, base, reads]);

  return index;
}
